import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ViewChild,
  ViewEncapsulation,
  effect,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { ListingCalendarDay } from 'src/shared/services/listing-calendar.service';
import { Lease, LeaseService } from 'src/shared/services/lease.service';

import { AddOwnerBlockPage } from 'src/pages/add-owner-block/add-owner-block';

import { OptimizerCalendarComponent } from './optimizer-calendar.component';
import { OptimizerDayDetailPage } from './day-detail/day-detail.page';
import { MockAddress } from '../mock-property';

@Component({
  selector: 'optimizer-section',
  standalone: true,
  imports: [TidyUiComponentsModule, SharedModule, OptimizerCalendarComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './optimizer-section.component.html',
  styleUrls: ['./optimizer-section.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class OptimizerSectionComponent {
  address = input.required<MockAddress>();
  // Demo accounts render the hardcoded mock month; real accounts fetch the
  // listing-calendar (gotidy #8271).
  mock = input<boolean>(true);
  compact = input<boolean>(false);
  showPricing = input<boolean>(true);
  interactive = input<boolean>(true);

  @ViewChild(OptimizerCalendarComponent) calendar?: OptimizerCalendarComponent;

  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly demoBlock = inject(DemoBlockService);
  private readonly navCtrl = inject(CustomNavController);
  private readonly storage = inject(TidyStorage);
  private readonly windowService = inject(WindowService);
  private readonly leaseService = inject(LeaseService);
  private readonly cdr = inject(ChangeDetectorRef);

  leases: Lease[] = [];

  /** True while a panel opened from this grid (day detail / block dates) is on screen. */
  private childPanelOpen = false;

  constructor() {
    effect(() => {
      // Re-read the address signal so the effect re-runs on address change.
      this.address();
      this.loadLeases();
    });
    // Subscribed ONCE here, and to afterPanelClosed() rather than
    // panelClosed(): panelClosed() filters the `isOpen$` BehaviorSubject's
    // *replayed current* value, and navigateTo() awaits storage before
    // flipping it to true — so subscribing right after that call saw `false`
    // and fired immediately at click time, consuming its take(1) and leaving
    // the actual close with no listener (the grid then only caught up on the
    // NEXT click — Trello 18344). afterPanelClosed() is pairwise-based, so it
    // emits only on a real true→false transition.
    this.rightSidePanelService.afterPanelClosed()
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.reloadIfDayPanelWasOpen());
  }

  // Public surface for parent toolbars/range toggles.
  prevMonth(): void { this.calendar?.prevMonth(); }
  nextMonth(): void { this.calendar?.nextMonth(); }
  get monthRangeLabel(): string { return this.calendar?.monthRangeLabel ?? ''; }

  onCellClick(day: ListingCalendarDay): void {
    if (!this.interactive()) { return; }
    this.rightSidePanelService.navigateTo(
      'optimizer/day',
      { day, addressId: this.address().id, addressName: this.address().display_name },
      OptimizerDayDetailPage,
    );
    // The day-detail panel can block the date or override its price; the
    // constructor subscription re-fetches the month once it closes.
    if (!this.mock()) { this.childPanelOpen = true; }
  }

  /**
   * Re-fetches the month if a panel opened from this grid was just dismissed.
   * Also called by the parent page's ionViewWillEnter: on mobile the day
   * detail and the block-dates form are pushed pages rather than the
   * right-side panel, so `isOpen$` never transitions and afterPanelClosed()
   * never emits there.
   */
  reloadIfDayPanelWasOpen(): void {
    if (!this.childPanelOpen) { return; }
    this.childPanelOpen = false;
    void this.calendar?.reload();
  }

  /**
   * Opens the Schedule page's "Block Dates" form — the same AddOwnerBlockPage
   * component and the same per-date `is_blocked` writes — so a range reserved
   * here is the same owner block the schedule shows.
   */
  async openBlockDates(): Promise<void> {
    if (!this.interactive()) { return; }
    // The demo's synthetic address ids don't exist in the real address list
    // the dialog loads, so without this the form would just fail its
    // "select a property" validation instead of showing the demo prompt.
    if (this.demoBlock.block()) { return; }
    await this.storage.save('preselected-property', this.address().id);
    // Clear any leftover prefill from a dialog the user cancelled — it would
    // otherwise prefill this open.
    await this.storage.delete('preselected-date');
    await this.storage.delete('preselected-end-date');
    if (this.windowService.isDesktopRes) {
      await this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.setDialogPageTitle('Block Dates');
      this.rightSidePanelService.openRightSidePanel(AddOwnerBlockPage);
    } else {
      // Mobile pushes it as a page — tell it to pop back here, not to Schedule.
      await this.storage.save('owner-block-return-page', 'optimizer');
      this.navCtrl.navigateForward('add-owner-block');
    }
    if (!this.mock()) { this.childPanelOpen = true; }
  }

  private async loadLeases(): Promise<void> {
    this.leases = await this.leaseService.getLeasesForAddress(this.address().id);
    this.cdr.markForCheck();
  }
}
